import { PageLayout, Badge, Button, Divider } from '../components';
import { useLanguage } from '../contexts/LanguageContext';
import { PARTICIPANT_OFFSET, VOTES_OFFSET } from '../constants';
import { useRegisterModal } from '../contexts/RegisterModalContext';

const FEATURED_CONTESTS = [
  {
    slug: 'milfu',
    participants: 24,
    votes: 1250,
    photos: [
      'photo_2026-01-05_22-43-27.jpg',
      'photo_2026-01-05_22-43-41.jpg',
      'photo_2026-01-05_22-43-47.jpg',
    ],
  },
  {
    slug: 'dancer2025',
    participants: 18,
    votes: 890,
    photos: [
      'photo_2026-01-05_22-43-52.jpg',
      'photo_2026-01-05_22-43-58.jpg',
      'photo_2026-01-05_22-44-02.jpg',
    ],
  },
  {
    slug: 'actress',
    participants: 12,
    votes: 560,
    photos: [
      'photo_2026-01-05_22-44-07.jpg',
      'photo_2026-01-05_22-44-11.jpg',
      'photo_2026-01-05_22-44-15.jpg',
    ],
  },
];

function ContestCard({ contest, onClickCard }) {
  const { t } = useLanguage();
  const { slug, participants, votes, photos } = contest;
  const displayParticipants = (participants || 0) + PARTICIPANT_OFFSET;
  const displayVotes = (votes || 0) + VOTES_OFFSET;
  const title = t(`home.contest.${slug}.title`);
  const description = t(`home.contest.${slug}.description`);

  return (
    <div
      onClick={onClickCard}
      className="cursor-pointer group flex flex-col rounded-xl xs:rounded-2xl border border-slate-200 bg-white/70 backdrop-blur shadow-sm overflow-hidden transition-transform hover:scale-[1.02] hover:border-slate-300"
    >
      {/* 3 фото превью */}
      <div className="h-28 xs:h-36 flex gap-0.5 overflow-hidden bg-slate-100">
        {photos.map((photo, i) => (
          <div key={i} className="flex-1 overflow-hidden">
            <img
              src={`/imgs/${photo}`}
              alt=""
              className="w-full h-full object-cover blur-lg group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        ))}
      </div>

      <div className="p-3 xs:p-5 flex flex-col flex-1">
        <h3 className="text-base xs:text-lg tablet:text-xl font-bold text-slate-900 group-hover:text-sky-700 transition-colors">
          {title}
        </h3>
        <p className="mt-1 xs:mt-2 text-xs xs:text-sm text-slate-600 leading-5 flex-1">{description}</p>

        <Divider className="my-3 xs:my-4" />

        <div className="flex justify-between text-xs xs:text-sm">
          <span className="text-slate-600">
            <span className="font-semibold text-slate-900">{t('home.card.participants', { count: displayParticipants })}</span>
          </span>
          <span className="text-slate-600">
            <span className="font-semibold text-slate-900">{t('home.card.votes', { count: displayVotes })}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t } = useLanguage();
  const { openRegister } = useRegisterModal();

  const handleFeatureClick = () => {
    openRegister('Чтобы пользоваться функциями сайта, нужно зарегистрироваться');
  };

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="flex flex-col justify-center items-center py-6 xs:py-8 tablet:py-10 px-4 xs:px-6 anim-fade-up">
        <h1 className="text-2xl xs:text-3xl 550:text-4xl tablet:text-5xl font-black tracking-tight text-center leading-tight bg-gradient-to-r from-sky-600 via-blue-600 to-sky-600 text-transparent bg-clip-text">
          {t('home.hero.title')}
        </h1>

        <p className="mt-3 xs:mt-4 text-sm xs:text-base tablet:text-lg text-slate-600 text-center max-w-lg px-2">
          {t('home.hero.subtitle')}
        </p>

        <Badge className="mt-4 xs:mt-6 text-xs xs:text-sm">{t('home.badge.activeContests')}</Badge>
      </section>

      {/* Contests Grid */}
      <section className="px-4 xs:px-6 pb-6 xs:pb-10 anim-fade-up" style={{ animationDelay: '60ms' }}>
        <div className="grid grid-cols-1 480:grid-cols-2 laptop:grid-cols-3 gap-4 xs:gap-6">
          {FEATURED_CONTESTS.map((contest, index) => (
            <div
              key={contest.slug}
              className="anim-fade-up"
              style={{ animationDelay: `${120 + index * 80}ms` }}
            >
              <ContestCard contest={contest} onClickCard={handleFeatureClick} />
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 xs:px-6 pb-6 xs:pb-10 anim-fade-up" style={{ animationDelay: '240ms' }}>
        <div className="rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 p-4 xs:p-6 text-center shadow-sm ring-1 ring-white/20">
          <h2 className="text-lg xs:text-xl tablet:text-2xl font-bold text-white">{t('home.cta.title')}</h2>
          <p className="mt-2 text-sm xs:text-base text-white/90">{t('home.cta.subtitle')}</p>
          <Button size="lg" className="mt-3 xs:mt-4 bg-white/20 hover:bg-white/30 text-sm xs:text-base" onClick={handleFeatureClick}>
            {t('home.cta.button')}
          </Button>
        </div>
      </section>
    </PageLayout>
  );
}
